import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  completeJourney,
  getStoredUser,
  isJourneyDone,
  JOURNEY_IDS,
  shouldStartJourney,
  skipJourney,
} from '@/lib/onboardingJourney';

/**
 * @param {string} journeyId
 * @param {{ ready?: boolean, onCompleteExtra?: () => void }} [options]
 */
export function useOnboardingJourney(journeyId, { ready = true, onCompleteExtra } = {}) {
  const user = getStoredUser();
  const userId = user?.id;
  const [active, setActive] = useState(false);
  const replayRef = useRef(false);

  const canAutoStart = useMemo(
    () => Boolean(userId && shouldStartJourney(userId, journeyId)),
    [userId, journeyId],
  );

  useEffect(() => {
    if (!ready || !canAutoStart) return undefined;
    replayRef.current = false;
    const t = window.setTimeout(() => setActive(true), 600);
    return () => window.clearTimeout(t);
  }, [ready, canAutoStart]);

  const startTour = useCallback(() => {
    replayRef.current = Boolean(userId && isJourneyDone(userId, journeyId));
    setActive(true);
  }, [userId, journeyId]);

  const finish = useCallback(
    (skipped) => {
      if (!userId) {
        setActive(false);
        return;
      }
      const replay = replayRef.current;
      if (!replay) {
        if (skipped) skipJourney(userId, journeyId);
        else completeJourney(userId, journeyId);
      }
      replayRef.current = false;
      setActive(false);

      if (!skipped && !replay) {
        if (journeyId === JOURNEY_IDS.DASHBOARD) {
          toast.success('Dashboard tour complete. Open the video editor when you are ready for the next tips.');
        } else if (journeyId === JOURNEY_IDS.VIDEO_EDITOR) {
          toast.success('You are all set — happy editing!');
        }
        onCompleteExtra?.();
      }
    },
    [userId, journeyId, onCompleteExtra],
  );

  return {
    active,
    setActive,
    startTour,
    handleComplete: () => finish(false),
    handleSkip: () => finish(true),
  };
}
